import React, { useMemo, useState } from 'react';
import { ChartData, ChartDataset, ChartOptions } from 'chart.js';
import { Bar, Scatter } from 'react-chartjs-2';
import { DistrictStudent } from 'services/districtAdminService';
import {
  USERMETADATA_METRICS,
  UserMetadataMetric,
} from './StudentComparisonChart';

interface StudentUsageChartProps {
  students?: DistrictStudent[];
}

const COLORS = [
  '#e6194b',
  '#3cb44b',
  '#ffe119',
  '#0082c8',
  '#f58231',
  '#911eb4',
  '#46f0f0',
  '#f032e6',
  '#d2f53c',
  '#fabebe',
  '#008080',
  '#e6beff',
  '#aa6e28',
  '#fffac8',
  '#800000',
  '#aaffc3',
  '#808000',
  '#ffd8b1',
  '#000080',
  '#808080',
];

export const USAGE_METRICS: UserMetadataMetric[] = [
  {
    accessor: 'days_online',
    display: 'Days online',
    scale: { min: 0, max: undefined },
  },
  {
    accessor: 'assignments_completed',
    display: 'Assignments completed',
    scale: { min: 0, max: undefined },
  },
  {
    accessor: 'evaluations_completed',
    display: 'Evaluations completed',
    scale: { min: 0, max: undefined },
  },
  {
    accessor: 'listenings_completed',
    display: 'Listenings completed',
    scale: { min: 0, max: undefined },
  },
  {
    accessor: 'practices_completed',
    display: 'Practices completed',
    scale: { min: 0, max: undefined },
  },
  {
    accessor: 'readings_completed',
    display: 'Readings completed',
    scale: { min: 0, max: undefined },
  },
];

const StudentUsageChart: React.FC<StudentUsageChartProps> = ({ students }) => {
  const [showScatter, setShowScatter] = useState<boolean>(false);
  const [metric, setMetric] = useState<UserMetadataMetric>(
    USERMETADATA_METRICS[0],
  );
  const [usageMetric, setUsageMetric] = useState<UserMetadataMetric>(
    USAGE_METRICS[0],
  );

  // bar chart data (unchanged)
  const snapshotChartData = useMemo<ChartData<'bar'>>(() => {
    const datasets: ChartDataset<'bar', number[]>[] = [];
    // const entries = gradebook.entries;
    // datasets.push({
    //   label: `Average`,
    //   data: [gradebook.averages.days_online],
    //   backgroundColor: COLORS[0],
    //   borderColor: COLORS[0],
    //   borderWidth: 1,
    // });
    students?.forEach((student, idx) => {
      datasets.push({
        label: `${student.user.username}`,
        data: [student.metadata[usageMetric.accessor] as number],
        backgroundColor: COLORS[(idx + 1) % COLORS.length],
        borderColor: COLORS[(idx + 1) % COLORS.length],
        borderWidth: 1,
      });
    });
    return {
      labels: ['Student'],
      datasets,
    };
  }, [students, usageMetric]);

  const snapshotChartOptions = useMemo<ChartOptions<'bar'>>(
    () => ({
      responsive: true,
      scales: {
        x: { type: 'category', title: { display: true, text: 'Metrics' } },
        y: {
          type: 'linear',
          beginAtZero: true,
          title: { display: true, text: usageMetric.display },
          min: usageMetric.scale.min,
          max: usageMetric.scale.max,
        },
      },
    }),
    [usageMetric],
  );

  const handleMetricChanged = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newMetric = USERMETADATA_METRICS.find(
      (a) => a.accessor === event.target.value,
    );
    if (!newMetric) return;
    setMetric(newMetric);
  };

  const handleUsageMetricChanged = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    const newMetric = USAGE_METRICS.find(
      (a) => a.accessor === event.target.value,
    );
    if (!newMetric) return;
    setUsageMetric(newMetric);
  };

  // scatter chart data
  const scatterChartData = useMemo<ChartData<'scatter'> | undefined>(() => {
    if (!students) return;
    const datasets = students.map((student, idx) => ({
      label: student.user.username,
      data: [
        {
          x: student.metadata[usageMetric.accessor] as number,
          y: student.metadata[metric.accessor] as number,
        },
      ],
      backgroundColor: COLORS[(idx + 1) % COLORS.length],
      pointRadius: 5,
    }));
    return { datasets };
  }, [students, metric, usageMetric]);

  const scatterChartOptions = useMemo<ChartOptions<'scatter'>>(
    () => ({
      responsive: true,
      scales: {
        x: {
          type: 'linear',
          position: 'bottom',
          title: { display: true, text: usageMetric.display },
        },
        y: {
          type: 'linear',
          title: { display: true, text: metric.display },
          min: metric.scale.min,
          max: metric.scale.max,
        },
      },
    }),
    [metric, usageMetric],
  );

  return (
    <>
      {showScatter && scatterChartData ? (
        <Scatter data={scatterChartData} options={scatterChartOptions} />
      ) : (
        <Bar data={snapshotChartData} options={snapshotChartOptions} />
      )}

      <div className="row spaced" style={{ padding: '10px' }}>
        <div className="row">
          <label className="label-normal">Usage metric</label>
          <select
            value={usageMetric.accessor}
            onChange={handleUsageMetricChanged}
          >
            {USAGE_METRICS.map((attr) => (
              <option key={attr.accessor} value={attr.accessor}>
                {attr.display}
              </option>
            ))}
          </select>
        </div>

        {showScatter && scatterChartData && (
          <div className="row">
            <label className="label-normal">Student metric</label>
            <select value={metric.accessor} onChange={handleMetricChanged}>
              {USERMETADATA_METRICS.map((attr) => (
                <option key={attr.accessor} value={attr.accessor}>
                  {attr.display}
                </option>
              ))}
            </select>
          </div>
        )}

        <label className="label-normal">
          <input
            type="checkbox"
            checked={showScatter}
            onChange={(e) => setShowScatter(e.target.checked)}
            style={{ marginRight: 8 }}
          />
          Show correlation
        </label>
      </div>
    </>
  );
};

export default StudentUsageChart;
